import { useEffect, useState } from 'react';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Inbox, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { api, API_BASE, getToken } from '../api';
import { pathToScreen } from '../labels';
import { SkeletonTable } from '../components/Skeleton';
import { utcToKst } from '../labels';

type ErrRow = {
  id: number;
  ts: string;
  url: string | null;
  method: string | null;
  status: number | null;
  message: string | null;
  stack: string | null;
  user_id: number | null;
  username: string | null;
  ip: string | null;
  user_agent: string | null;
  recent_1h_count: number | null;
};

const PAGE_SIZES = [50, 100, 200, 500];

// 화면 표기는 KST — 필터 input 문자열도 KST로 넣고 kstStrToUtc로 서버 전송 시 UTC 환산.
function kstNow(): string {
  const kst = new Date(Date.now() + 9 * 3600_000);
  return kst.toISOString().replace('T', ' ').slice(0, 19);
}
function kstShiftHours(h: number): string {
  const kst = new Date(Date.now() + h * 3600_000 + 9 * 3600_000);
  return kst.toISOString().replace('T', ' ').slice(0, 19);
}
function kstStrToUtc(s: string): string {
  if (!s) return s;
  const iso = /[TZ]|[+-]\d{2}:\d{2}$/.test(s) ? s : s.replace(' ', 'T') + '+09:00';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return s;
  return d.toISOString().replace('T', ' ').slice(0, 19);
}

// URL 경로에서 MID·주문번호 등 관련 대상 추출.
// 예: /merchants/gpay7476dm/tx-summary → { kind: 'MID', value: 'gpay7476dm' }
// 예: /transactions/ord-abc-123 → { kind: '주문번호', value: 'ord-abc-123' }
function extractTarget(url: string | null): { kind: string; value: string } | null {
  if (!url) return null;
  try {
    const p = url.startsWith('http') ? new URL(url).pathname + new URL(url).search : url;
    let m: RegExpMatchArray | null;
    m = p.match(/\/merchants\/([^/?#]+)/); if (m && !['new', 'pending-info'].includes(m[1])) return { kind: 'MID', value: decodeURIComponent(m[1]) };
    m = p.match(/\/transactions\/([^/?#]+)/); if (m) return { kind: '주문번호', value: decodeURIComponent(m[1]) };
    m = p.match(/\/transfers\/([^/?#]+)/); if (m) return { kind: '이체ID', value: decodeURIComponent(m[1]) };
    m = p.match(/\/users\/(\d+)/); if (m) return { kind: '사용자ID', value: m[1] };
    m = p.match(/[?&]mcht_id=([^&#]+)/); if (m) return { kind: 'MID', value: decodeURIComponent(m[1]) };
    m = p.match(/[?&]ord_num=([^&#]+)/); if (m) return { kind: '주문번호', value: decodeURIComponent(m[1]) };
    m = p.match(/\/merchant-renewals\/([^/?#]+)/); if (m && !['run-auto-end', 'auto-end-preview'].includes(m[1])) return { kind: 'MID(계약)', value: decodeURIComponent(m[1]) };
    return null;
  } catch { return null; }
}

export default function SecurityErrors() {
  const [data, setData] = useState<ErrRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [statusClass, setStatusClass] = useState<'' | '4xx' | '5xx'>('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (statusClass) qs.set('status_class', statusClass);
      if (q) qs.set('q', q);
      if (from) qs.set('from', kstStrToUtc(from));
      if (to) qs.set('to', kstStrToUtc(to));
      qs.set('limit', String(limit));
      qs.set('offset', String(offset));
      const r = await api<{ data: ErrRow[]; total: number }>('GET', `/security/errors?${qs.toString()}`);
      setData(r.data ?? []);
      setTotal(r.total ?? 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [limit, offset, statusClass]);

  const applyPreset = (h: number) => {
    setFrom(kstShiftHours(-h));
    setTo(kstNow());
    setOffset(0);
    setTimeout(load, 0);
  };

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <h2>오류 로그</h2>
      <div className="card">
        <div className="toolbar" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label>검색 (메시지 · URL)</label>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="키워드" style={{ width: '100%' }} />
          </div>
          <div>
            <label>HTTP 상태</label>
            <input value={status} onChange={e => setStatus(e.target.value)} placeholder="예: 500" style={{ width: 100 }} />
          </div>
          <div>
            <label>상태 계열</label>
            <select value={statusClass} onChange={e => { setStatusClass(e.target.value as any); setOffset(0); }}>
              <option value="">전체</option>
              <option value="4xx">4xx 클라이언트 오류</option>
              <option value="5xx">5xx 서버 오류</option>
            </select>
          </div>
          <div>
            <label>시작일시 (KST)</label>
            <input value={from} onChange={e => setFrom(e.target.value)} placeholder="YYYY-MM-DD HH:MM:SS" />
          </div>
          <div>
            <label>종료일시 (KST)</label>
            <input value={to} onChange={e => setTo(e.target.value)} placeholder="YYYY-MM-DD HH:MM:SS" />
          </div>
          <button className="btn" onClick={() => { setOffset(0); load(); }}>조회</button>
          <button className="btn secondary" onClick={() => {
            setStatus(''); setStatusClass(''); setQ(''); setFrom(''); setTo(''); setOffset(0);
          }}>초기화</button>
          <button className="btn secondary"
                  onClick={async () => {
                    const qs = new URLSearchParams();
                    if (status) qs.set('status', status);
                    if (statusClass) qs.set('status_class', statusClass);
                    if (q) qs.set('q', q);
                    if (from) qs.set('from', kstStrToUtc(from));
                    if (to) qs.set('to', kstStrToUtc(to));
                    const tok = getToken();
                    const res = await fetch(`${API_BASE}/security/errors.csv?${qs.toString()}`, {
                      headers: tok ? { Authorization: `Bearer ${tok}` } : undefined,
                    });
                    if (!res.ok) { alert(`다운로드 실패: HTTP ${res.status}`); return; }
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    const tag = from && to ? `${from.slice(0, 10)}_${to.slice(0, 10)}` : new Date().toISOString().slice(0, 10);
                    a.download = `서포트리_오류로그_${tag}.csv`;
                    document.body.appendChild(a); a.click(); a.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  }}
                  title="현재 필터 조건의 오류 로그를 CSV로 다운로드 (최대 50,000행, UTF-8 BOM)">
            <FileSpreadsheet size={12} /> CSV 다운로드
          </button>
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>빠른 기간</span>
          {[
            { l: '오늘', h: 24 },
            { l: '최근 24시간', h: 24 },
            { l: '최근 7일', h: 24 * 7 },
            { l: '최근 30일', h: 24 * 30 },
          ].map(p => (
            <button key={p.l} onClick={() => applyPreset(p.h)} className="filter-ghost" style={{ fontSize: 12, padding: '4px 10px' }}>{p.l}</button>
          ))}
        </div>

        {loading ? <SkeletonTable rows={10} cols={7} showHeader /> : data.length === 0 ? (
          <div className="empty-state">
            <Inbox className="empty-icon" size={36} strokeWidth={1.6} />
            <h4>오류 없음</h4>
            <p>선택한 조건에 해당하는 오류 로그가 없습니다.</p>
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th className="num nowrap">오류 ID</th>
                  <th className="nowrap">시각 (KST)</th>
                  <th className="nowrap">상태</th>
                  <th className="nowrap">메소드</th>
                  <th>관련 화면 · URL</th>
                  <th className="nowrap">관련 대상</th>
                  <th>메시지</th>
                  <th className="nowrap">사용자</th>
                  <th className="nowrap">IP</th>
                  <th className="nowrap">반복(1h)</th>
                  <th className="nowrap">스택</th>
                </tr></thead>
                <tbody>
                  {data.map(e => {
                    const screen = pathToScreen(e.url);
                    const errId = `ERR-${(e.ts ?? '').replace(/[^0-9]/g, '').slice(0, 14)}-${String(e.id).padStart(5, '0')}`;
                    const repeat = Number(e.recent_1h_count ?? 0);
                    return (
                      <tr key={e.id}>
                        <td className="nowrap" style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 11 }}
                            title="복사해 개발자에게 전달하면 동일 오류를 바로 조회할 수 있습니다.">
                          {errId}
                        </td>
                        <td className="nowrap" style={{ fontFamily: 'monospace' }} title={`서버 저장(UTC): ${e.ts}`}>{utcToKst(e.ts)}</td>
                        <td className="nowrap">
                          <span className="chip"
                                style={{ background: '#fee2e2', color: '#7f1d1d', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <AlertTriangle size={11} strokeWidth={2.4} />
                            <span>{e.status ?? '-'}</span>
                          </span>
                        </td>
                        <td className="nowrap" style={{ fontFamily: 'monospace' }}>{e.method ?? '-'}</td>
                        <td>
                          {screen ? (
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>{screen}</div>
                              <div className="muted" style={{ fontFamily: 'monospace', fontSize: 11 }} title={e.url ?? undefined}>{e.url ?? '-'}</div>
                            </div>
                          ) : (
                            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{e.url ?? '-'}</span>
                          )}
                        </td>
                        <td className="nowrap">
                          {(() => {
                            const t = extractTarget(e.url);
                            if (!t) return <span className="muted">-</span>;
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{t.kind}</span>
                                <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{t.value}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.message ?? undefined}>{e.message ?? '-'}</td>
                        <td className="nowrap">
                          {e.username
                            ? <span style={{ fontFamily: 'monospace' }}>{e.username}{e.user_id ? <span className="muted"> #{e.user_id}</span> : null}</span>
                            : <span className="muted">-</span>}
                        </td>
                        <td className="nowrap" style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{e.ip ?? '-'}</td>
                        <td className="nowrap">
                          {repeat > 1
                            ? <span className="chip" style={{ background: '#fef3c7', color: '#854d0e', fontWeight: 700 }} title="같은 URL · 상태 조합이 최근 1시간 발생 횟수">{repeat}건</span>
                            : <span className="muted">1건</span>}
                        </td>
                        <td className="nowrap">
                          {e.stack
                            ? <button className="btn secondary sm" onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                                {expanded === e.id ? '닫기' : '보기'}
                              </button>
                            : <span className="muted">-</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {expanded != null && (() => {
              const cur = data.find(d => d.id === expanded);
              if (!cur?.stack) return null;
              return (
                <div style={{
                  marginTop: 12, padding: 14,
                  background: '#0f172a', color: '#e2e8f0',
                  fontFamily: 'monospace', fontSize: 12, lineHeight: 1.55,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  borderRadius: 8, maxHeight: 360, overflow: 'auto',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ color: '#94a3b8' }}>#{cur.id} stack trace (기술 상세)</span>
                    <button
                      className="btn secondary sm"
                      style={{ background: '#1e293b', color: '#e2e8f0', borderColor: '#334155' }}
                      onClick={() => setExpanded(null)}
                    >닫기</button>
                  </div>
                  {cur.stack}
                </div>
              );
            })()}
          </>
        )}

        <div className="toolbar" style={{ justifyContent: 'space-between', marginTop: 12 }}>
          <div className="muted">총 <b style={{ color: 'var(--leaf-800)' }}>{total.toLocaleString('ko-KR')}</b>건 · {page}/{totalPages} 페이지</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setOffset(0); }}>
              {PAGE_SIZES.map(n => <option key={n} value={n}>{n}/페이지</option>)}
            </select>
            <button className="btn secondary sm" onClick={() => setOffset(0)} disabled={offset === 0}><ChevronsLeft size={14} /></button>
            <button className="btn secondary sm" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}><ChevronLeft size={14} /></button>
            <button className="btn secondary sm" onClick={() => setOffset(offset + limit)} disabled={page >= totalPages}><ChevronRight size={14} /></button>
            <button className="btn secondary sm" onClick={() => setOffset((totalPages - 1) * limit)} disabled={page >= totalPages}><ChevronsRight size={14} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
